import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';

import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { Store } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { Observable, map } from 'rxjs';

import { IBetslipMessages } from '../../../base/models/betslip-messages';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { PickOddsState } from '../../../core/picks/pick-models';
import { BetslipGeneratorContextType } from '../../bet-generation/models';
import { FreeBetToken, RewardTokenContext } from '../../reward-tokens/reward-tokens.model';
import { selectFreeBetTokenFactory } from '../../reward-tokens/selectors';
import { parseStakeFieldStake } from '../../stake/utils';
import { BetslipSummaryService } from '../../summary/services/betslip-summary.service';
import BetslipSingleBetActions from '../actions';
import { ISingleBetPickState } from '../state';

@Component({
    selector: 'bs-digital-single-bet-pick-info-column',
    templateUrl: './single-bet-pick-info-column.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: [
        '../../picks/components/digital/base-bet-type-pick.component.scss',
        '../../picks/components/digital/base-bet-type-pick.component.linear.scss',
        './single-bet-pick-info-column.component.scss',
        './single-bet-pick-info-column.component.linear.scss',
    ],
    standalone: false,
})
export default class SingleBetPickInfoColumnComponent implements OnInit {
    @Input() pick: BetslipPick;
    @Input() singleBetPick: ISingleBetPickState;
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() isMultiSingleBet: boolean;
    @Input() alwaysShowStakeInput: boolean;
    @Input() isLinear: boolean;
    @Input() messages: IBetslipMessages;
    @Input() pickChange$!: Observable<BetslipPick>;
    @Input() pickClass: string;
    @Input() pickTemplate: TemplateRef<any>;
    @Output() clickInfo = new EventEmitter<Event>();
    possibleWinnings$: Observable<Decimal | null>;
    freeBetToken$: Observable<FreeBetToken | null>;
    isTaxationApplied: boolean | undefined;

    readonly betslipType = BetslipType.Single;

    constructor(
        private store: Store<IBetslipRootState>,
        private summaryService: BetslipSummaryService,
    ) {}

    ngOnInit(): void {
        if (!this.isLinear) {
            this.possibleWinnings$ = this.summaryService.getSummary$(BetslipGeneratorContextType.Betslip).pipe(
                map(([_, betslipSummary]) => {
                    const summary = betslipSummary.slipSummaries.find(
                        (s) =>
                            // s.slip.systemType === SystemType.SystemSingle &&
                            Object.keys(s.slip.slipPicks).length === 1 && this.pick.id.toString() in s.slip.slipPicks,
                    );
                    if (!summary) {
                        return new Decimal(0);
                    }
                    this.isTaxationApplied = betslipSummary.taxation?.amount?.greaterThan(0) || betslipSummary.taxation?.isPlaceholderTax;

                    const boostedWinnings = summary.rewardTokenModifications?.oddsBoost?.boostedWinnings;

                    if (boostedWinnings?.greaterThan(0)) {
                        return boostedWinnings;
                    }

                    return summary.possibleWinnings && summary.possibleWinnings.greaterThan(0)
                        ? summary.possibleWinnings.minus(summary.taxation?.amount || 0)
                        : null;
                }),
            );
        }

        this.freeBetToken$ = this.store.select(selectFreeBetTokenFactory(this.tokenContext));
    }

    onStakeChange($stake: string): void {
        const stake = parseStakeFieldStake($stake);

        this.store.dispatch(
            BetslipSingleBetActions.setSingleBetPickStake({
                pickId: this.pick.id.toString(),
                silent: false,
                stake,
                stakeStr: $stake,
            }),
        );
    }

    get marketSubtypeLogoTooltipBoundaries(): string {
        return this.isLinear ? 'bs-betslip-linear-edit-state' : 'bs-betslip-edit-state';
    }

    get isMultiplierVisible(): boolean {
        return this.singleBetPick.isEachWay || this.pick.betCount() > 1;
    }

    get multiplierValue(): string {
        const multiplier = this.singleBetPick.isEachWay ? 2 : this.pick.betCount();

        return `${multiplier}x`;
    }

    get hasStakeInput(): boolean {
        return (
            this.pick.oddsState !== PickOddsState.Closed &&
            (!this.isLinear || this.pick.oddsState !== PickOddsState.Locked) &&
            (this.alwaysShowStakeInput || (this.isMultiSingleBet && this.pick.oddsState === PickOddsState.Open))
        );
    }

    get isPickOddsPriceBoosted(): boolean {
        return this.isLinear && !!this.pick.boostedPriceDetails?.isPriceBoosted;
    }

    get isPickOddsStateLocked(): boolean {
        return (this.isLinear || this.isMultiSingleBet) && this.pick.oddsState === PickOddsState.Locked;
    }

    get isStakeInputDisabled(): boolean {
        return this.alwaysShowStakeInput && this.pick.oddsState !== PickOddsState.Open;
    }

    preventClick(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    get tokenContext(): RewardTokenContext {
        return {
            betslipType: BetslipType.Single,
            pickId: this.pick.id,
        };
    }
}
